import * as React from "react";
import { Dialog } from "./dialog";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type CloseFunction = () => void;

type ModalDefinition<P = Record<string, never>> = {
  readonly _brand: "ModalDefinition";
  readonly component: React.FC<P & { close: CloseFunction; }>;
};

type ModalEntry = {
  readonly definition: ModalDefinition<any>;
  readonly props: Record<string, unknown>;
};

type ModalContextValue = {
  open: <P>(definition: ModalDefinition<P>, props: P) => void;
  close: () => void;
};

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ModalContext = React.createContext<ModalContextValue | null>(null);

// ---------------------------------------------------------------------------
// defineModal
// ---------------------------------------------------------------------------

export function defineModal<P = Record<string, never>>(
  component: React.FC<P & { close: CloseFunction; }>,
): ModalDefinition<P> {
  return { _brand: "ModalDefinition", component };
}

// ---------------------------------------------------------------------------
// useModal
// ---------------------------------------------------------------------------

export function useModal(): ModalContextValue {
  const context = React.useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within a <ModalProvider>.");
  }
  return context;
}

// ---------------------------------------------------------------------------
// ModalProvider + Outlet
// ---------------------------------------------------------------------------

export const ModalProvider: React.FC<{ children: React.ReactNode; }> = ({
  children,
}) => {
  const [entry, setEntry] = React.useState<ModalEntry | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);

  const close = React.useCallback(() => {
    setDialogOpen(false);
    // Clear entry after animation has time to complete
    setTimeout(() => setEntry(null), 200);
  }, []);

  const contextValue = React.useMemo<ModalContextValue>(
    () => ({
      open: (definition, props) => {
        setEntry({ definition, props: props as Record<string, unknown> });
        setDialogOpen(true);
      },
      close,
    }),
    [close],
  );

  const handleOpenChange = React.useCallback(
    (open: boolean) => {
      if (!open) close();
    },
    [close],
  );

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
        {entry && <entry.definition.component {...entry.props} close={close} />}
      </Dialog>
    </ModalContext.Provider>
  );
};
